import React, { useState, useEffect } from 'react';
import { Package, Plus, TrendingUp, BarChart3, AlertTriangle, History, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CountUp from './CountUp';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const WarehouseManagement = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalItems: 0,
    lowStock: 0,
    totalProjects: 0,
    totalValue: 0,
    totalShipments: 0,
    totalVehicles: 0,
    activeAssignments: 0
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActivitiesModal, setShowActivitiesModal] = useState(false);
  const [allActivities, setAllActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  useEffect(() => {
    fetchWarehouseData();
  }, []);

  const fetchWarehouseData = async () => {
    try {
      setLoading(true);
      
      // Fetch inventory summary
      const summaryResponse = await axios.get(`${API_BASE_URL}/api/warehouse/analytics/inventory_summary/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setStats({
        totalItems: summaryResponse.data.total_items || 0,
        lowStock: summaryResponse.data.low_stock_items || 0,
        totalProjects: 0, // Will be updated with project data
        totalValue: summaryResponse.data.total_value || 0,
        totalVehicles: 0,
        activeAssignments: 0
      });

      // Fetch recent stock movements
      const movementsResponse = await axios.get(`${API_BASE_URL}/api/warehouse/stock-movements/?ordering=-created_at&limit=10`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const movementsData = Array.isArray(movementsResponse.data) ? movementsResponse.data : 
                           movementsResponse.data.results ? movementsResponse.data.results : [];
      const formattedActivities = movementsData.map(movement => ({
        id: movement.id,
        action: movement.movement_type.replace('_', ' '),
        item: movement.item_name,
        quantity: Math.abs(movement.quantity),
        project: movement.project_name,
        date: new Date(movement.created_at).toLocaleDateString(),
        user: movement.created_by_name || 'System'
      }));
      setRecentActivities(formattedActivities);

      // Fetch active projects with warehouse activity from issues
      try {
        const issuesResponse = await axios.get(`${API_BASE_URL}/api/warehouse/warehouse-issues/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const issuesData = Array.isArray(issuesResponse.data) ? issuesResponse.data : 
                          issuesResponse.data.results ? issuesResponse.data.results : [];
        const uniqueProjects = new Set();
        issuesData.forEach(issue => {
          if (issue.project) {
            uniqueProjects.add(issue.project);
          }
        });
        console.log('Unique projects from issues:', uniqueProjects.size); // Debug log
        setStats(prev => ({
          ...prev,
          totalProjects: uniqueProjects.size
        }));
      } catch (projectError) {
        console.error('Error fetching project data:', projectError);
        // Fallback to total projects if warehouse projects fail
        try {
          const allProjectsResponse = await axios.get(`${API_BASE_URL}/api/projects/`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          const projectsData = Array.isArray(allProjectsResponse.data) ? allProjectsResponse.data : 
                              allProjectsResponse.data.results ? allProjectsResponse.data.results : [];
          setStats(prev => ({
            ...prev,
            totalProjects: projectsData.length
          }));
        } catch (fallbackError) {
          console.error('Error fetching fallback project data:', fallbackError);
        }
      }

      // Fetch shipments count
      try {
        const shipmentsResponse = await axios.get(`${API_BASE_URL}/api/warehouse/shipments/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const shipmentsData = Array.isArray(shipmentsResponse.data) ? shipmentsResponse.data : 
                             shipmentsResponse.data.results ? shipmentsResponse.data.results : [];
        setStats(prev => ({
          ...prev,
          totalShipments: shipmentsData.length
        }));
      } catch (shipmentError) {
        console.error('Error fetching shipment data:', shipmentError);
      }

      // Fetch fleet data
      try {
        const [vehiclesResponse, assignmentsResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/cars/`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
          }),
          axios.get(`${API_BASE_URL}/api/warehouse/vehicle-assignments/?status=assigned`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
          })
        ]);

        const vehiclesData = vehiclesResponse.data.results || vehiclesResponse.data || [];
        const assignmentsData = assignmentsResponse.data.results || assignmentsResponse.data || [];

        console.log('Fleet Debug - Vehicles API response:', vehiclesResponse.data);
        console.log('Fleet Debug - Vehicles data:', vehiclesData);
        console.log('Fleet Debug - Vehicles count:', Array.isArray(vehiclesData) ? vehiclesData.length : 'Not an array');
        console.log('Fleet Debug - Assignments data:', assignmentsData);

        setStats(prev => ({
          ...prev,
          totalVehicles: Array.isArray(vehiclesData) ? vehiclesData.length : 0,
          activeAssignments: Array.isArray(assignmentsData) ? assignmentsData.length : 0
        }));
      } catch (fleetError) {
        console.error('Error fetching fleet data:', fleetError);
        // Reset fleet stats to 0 on error
        setStats(prev => ({
          ...prev,
          totalVehicles: 0,
          activeAssignments: 0
        }));
      }

    } catch (error) {
      console.error('Error fetching warehouse data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllActivities = async (limit = 20) => {
    setActivitiesLoading(true);
    try {
      // Fetch both stock movements and warehouse issues for comprehensive activity list
      const [stockResponse, issuesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/warehouse/stock-movements/?ordering=-created_at&limit=${Math.ceil(limit/2)}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        }),
        axios.get(`${API_BASE_URL}/api/warehouse/warehouse-issues/?ordering=-created_at&limit=${Math.ceil(limit/2)}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        })
      ]);

      const stockMovements = Array.isArray(stockResponse.data) ? stockResponse.data : 
                            stockResponse.data.results ? stockResponse.data.results : [];
      const warehouseIssues = Array.isArray(issuesResponse.data) ? issuesResponse.data : 
                             issuesResponse.data.results ? issuesResponse.data.results : [];

      // Format stock movements
      const stockActivities = stockMovements.map(movement => ({
        id: `stock_${movement.id}`,
        action: movement.movement_type.replace('_', ' '),
        item: movement.item_name,
        quantity: Math.abs(movement.quantity),
        project: movement.project_name,
        recipient: null,
        serialNumbers: [],
        date: new Date(movement.created_at).toLocaleDateString(),
        time: new Date(movement.created_at).toLocaleTimeString(),
        user: movement.created_by_name || 'System',
        type: 'stock_movement'
      }));

      // Format warehouse issues  
      const issueActivities = warehouseIssues.map(issue => ({
        id: `issue_${issue.id}`,
        action: 'Issued',
        item: `${issue.items?.length || 0} items`,
        quantity: issue.items?.reduce((sum, item) => sum + (item.quantity_issued || 0), 0) || 0,
        project: issue.project_name,
        recipient: issue.recipient_name || issue.assigned_to_person || 'Unknown',
        serialNumbers: issue.items?.flatMap(item => 
          item.individual_items?.map(indItem => 
            Object.entries(indItem).find(([key, value]) => 
              key.toLowerCase().includes('serial') && value
            )?.[1]
          ).filter(Boolean) || []
        ) || [],
        date: new Date(issue.created_at).toLocaleDateString(),
        time: new Date(issue.created_at).toLocaleTimeString(),
        user: issue.issued_by_name || 'System',
        type: 'warehouse_issue'
      }));

      // Combine and sort by date
      const allActivities = [...stockActivities, ...issueActivities]
        .sort((a, b) => new Date(`${b.date} ${b.time}`) - new Date(`${a.date} ${a.time}`))
        .slice(0, limit);

      setAllActivities(allActivities);
    } catch (error) {
      console.error('Error fetching all activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const handleViewAllActivities = () => {
    setShowActivitiesModal(true);
    fetchAllActivities();
  };

  const handleLoadMoreActivities = () => {
    fetchAllActivities(allActivities.length + 20);
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'addItem':
        navigate('/warehouse/inventory');
        break;
      case 'issueToProject':
        navigate('/warehouse/issue');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-steel-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-steel-300 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-steel-300 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-steel-300 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-steel-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Warehouse Management
              </h1>
              <p className="text-slate-600 mt-2">Manage inventory, track items, and analyze project usage</p>
            </div>
            <div className="flex space-x-3">
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-brand-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Items</p>
                <p className="text-3xl font-bold text-slate-900">
                  <CountUp to={stats.totalItems} duration={1.5} separator="," />
                </p>
              </div>
              <div className="h-12 w-12 bg-brand-100 rounded-xl flex items-center justify-center">
                <Package className="h-6 w-6 text-brand-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-brand-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Low Stock Items</p>
                <p className="text-3xl font-bold text-orange-600">
                  <CountUp to={stats.lowStock} duration={1.5} separator="," />
                </p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-brand-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Active Projects</p>
                <p className="text-3xl font-bold text-green-600">
                  <CountUp to={stats.totalProjects} duration={1.5} separator="," />
                </p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-brand-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button 
                onClick={() => handleQuickAction('addItem')}
                className="w-full flex items-center justify-between p-3 bg-brand-50 text-brand-700 rounded-xl hover:bg-brand-100 transition-colors"
              >
                <span className="flex items-center">
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Item
                </span>
              </button>
              <button 
                onClick={() => navigate('/warehouse/inventory')}
                className="w-full flex items-center justify-between p-3 bg-green-50 text-green-700 rounded-xl hover:bg-green-100 transition-colors"
              >
                <span className="flex items-center">
                  <Package className="h-4 w-4 mr-2" />
                  Inventory
                </span>
              </button>
              <button
                onClick={() => navigate('/warehouse/history')}
                className="w-full flex items-center justify-between p-3 bg-brand-50 text-brand-700 rounded-xl hover:bg-brand-100 transition-colors"
              >
                <span className="flex items-center">
                  <History className="h-4 w-4 mr-2" />
                  Allocations
                </span>
              </button>
              <button 
                onClick={() => navigate('/warehouse/settings')}
                className="w-full flex items-center justify-between p-3 bg-steel-100 text-slate-700 rounded-xl hover:bg-steel-200 transition-colors"
              >
                <span className="flex items-center">
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </span>
              </button>
            </div>
          </div>

          {/* Recent Activities */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6 border border-brand-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Recent Activities</h3>
            <div className="max-h-80 overflow-y-auto space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-center justify-between p-3 bg-steel-50 rounded-xl">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">
                      {activity.action}: {activity.item}
                    </p>
                    <p className="text-xs text-slate-500">
                      {activity.project && `Project: ${activity.project} • `}
                      Quantity: {activity.quantity} • By: {activity.user}
                    </p>
                  </div>
                  <span className="text-xs text-slate-500">{activity.date}</span>
                </div>
              ))}
              {recentActivities.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <p>No recent activities found.</p>
                </div>
              )}
            </div>
            <button 
              onClick={handleViewAllActivities}
              className="w-full mt-4 text-sm text-brand-600 hover:text-brand-700 font-medium"
            >
              View All Activities
            </button>
          </div>
        </div>

        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div 
            onClick={() => navigate('/warehouse/inventory')}
            className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-xl shadow-sm p-8 border border-brand-200 hover:shadow-lg transition-all duration-300 cursor-pointer group"
          >
            <div className="flex items-center mb-6">
              <div className="h-14 w-14 bg-brand-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Package className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 ml-4">Inventory Management</h3>
            </div>
            <p className="text-slate-700 mb-6 text-lg leading-relaxed">Add, edit, and track all warehouse items. Monitor stock levels and manage your complete inventory.</p>
            <button className="text-brand-700 hover:text-brand-800 font-semibold text-lg group-hover:translate-x-2 transition-transform duration-300">
              Manage Inventory →
            </button>
          </div>

          <div 
            onClick={() => navigate('/warehouse/history')}
            className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm p-8 border border-green-200 hover:shadow-lg transition-all duration-300 cursor-pointer group"
          >
            <div className="flex items-center mb-6">
              <div className="h-14 w-14 bg-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <History className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 ml-4">Allocations</h3>
            </div>
            <p className="text-slate-700 mb-6 text-lg leading-relaxed">View shipment history, track deliveries, and manage all warehouse transactions.</p>
            <button className="text-green-700 hover:text-green-900 font-semibold text-lg group-hover:translate-x-2 transition-transform duration-300">
              View Shipments →
            </button>
          </div>
        </div>

        {/* Activities Modal */}
        {showActivitiesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] flex flex-col">
              <div className="p-6 border-b border-steel-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-slate-900">All Activities</h2>
                  <button
                    onClick={() => setShowActivitiesModal(false)}
                    className="text-slate-500 hover:text-slate-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6">
                {activitiesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                    <span className="ml-3 text-slate-600">Loading activities...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {allActivities.map((activity, index) => (
                      <div key={`${activity.id}-${index}`} className="flex items-center justify-between p-4 bg-steel-50 rounded-xl hover:bg-steel-100 transition-colors">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <p className="text-sm font-medium text-slate-900">
                              {activity.action}: {activity.item}
                            </p>
                            {activity.type === 'warehouse_issue' && (
                              <span className="px-2 py-1 text-xs bg-brand-100 text-brand-800 rounded-full">
                                Issue
                              </span>
                            )}
                            {activity.type === 'stock_movement' && (
                              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                Movement
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 space-y-1">
                            <div>
                              {activity.project && `Project: ${activity.project} • `}
                              Quantity: {activity.quantity} • By: {activity.user}
                            </div>
                            {activity.recipient && (
                              <div className="text-brand-600">
                                → Issued to: {activity.recipient}
                              </div>
                            )}
                            {activity.serialNumbers && activity.serialNumbers.length > 0 && (
                              <div className="text-purple-600">
                                Serial Numbers: {activity.serialNumbers.join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-slate-500 block">{activity.date}</span>
                          <span className="text-xs text-slate-400 block">{activity.time}</span>
                        </div>
                      </div>
                    ))}
                    {allActivities.length === 0 && (
                      <div className="text-center py-8 text-slate-500">
                        <p>No activities found.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="p-6 border-t border-steel-200 flex justify-between items-center">
                <span className="text-sm text-slate-600">
                  Showing {allActivities.length} activities
                </span>
                <button
                  onClick={handleLoadMoreActivities}
                  disabled={activitiesLoading}
                  className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20 disabled:bg-steel-400 disabled:cursor-not-allowed transition-colors"
                >
                  {activitiesLoading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default WarehouseManagement;